import React, { useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { TurboLoader } from "../TurboLoader";
import { useTokenRenewal } from "../../hooks/useTokenRenewal";
import { useFlehsTokenAcquisition } from "../../hooks/useFlehsTokenAcquisition";
import { useMsal } from "@azure/msal-react";
import { useAuthenticatedApiClient } from "../../hooks/useAuthenticatedApiClient";
import { useLookupStore, setLookupApiClient } from "../../stores/lookupStore";

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  // Enable automatic token renewal
  useTokenRenewal();

  // Acquire FLEHS token after login
  useFlehsTokenAcquisition();

  // Get auth state and API client
  const { accounts } = useMsal();
  const apiClient = useAuthenticatedApiClient("flehsApi");

  // Track if we've started loading to prevent duplicate calls
  const loadingStartedRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  // Get progress state for turbo loader
  const progress = useLookupStore((state) => state.progress);
  const progressPercent =
    progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : 0;

  // Load all lookups once when user authenticates
  // (Admin user data is loaded in root loader before this)
  useEffect(() => {
    const currentUserId =
      accounts.length > 0 ? (accounts[0].idTokenClaims as any)?.oid : null;

    // Reset if user changed or logged out
    if (currentUserId !== currentUserIdRef.current) {
      loadingStartedRef.current = false;
      currentUserIdRef.current = currentUserId;
      useLookupStore.getState().reset();
    }

    if (accounts.length > 0 && apiClient && !loadingStartedRef.current) {
      loadingStartedRef.current = true;
      setLookupApiClient(apiClient);
      useLookupStore.getState().loadAllModules();
    }
  }, [accounts.length, apiClient]);

  return (
    <div className="d-flex flex-column min-vh-100 app-layout">
      <TurboLoader progress={progressPercent} visible={progress.isLoading} />
      <Navigation />

      <main className="flex-grow-1 app-main">
        <Container className="py-4">{children}</Container>
      </main>

      <footer className="mt-auto footer-custom">
        <Footer />
      </footer>
    </div>
  );
};
